if (!customElements.get('horizontal-scroll-showcase')) {
  class HorizontalScrollShowcase extends HTMLElement {
    constructor() {
      super();
      if (window.initLazyScript) {
        window.initLazyScript(this, this.init.bind(this));
      } else {
        this.init();
      }
    }

    init() {
      this.scrollSpace = this.querySelector('[data-scroll-space]');
      this.sticky = this.querySelector('.hss__sticky');
      this.track = this.querySelector('[data-track]');
      this.progress = this.querySelector('[data-progress]');
      this.counter = this.querySelector('[data-counter]');
      this.counterCurrent = this.querySelector('[data-counter-current]');
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.slides = this.querySelectorAll('.hss__slide');
      this.minDesktop = Number(this.dataset.desktopMin || 990);
      this.sliderEnabled = this.dataset.sliderEnabled !== 'false';
      this.desktopQuery = window.matchMedia(`(min-width: ${this.minDesktop}px)`);
      this.currentIndex = 0;
      this.ticking = false;

      if (!this.track || !this.progress || !this.scrollSpace) return;

      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);

      if (this.sliderEnabled) {
        this.desktopQuery.addEventListener('change', this.onBreakpointChange);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
        if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrevClick);
        if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNextClick);
      }

      this.setup();
    }

    setup() {
      this.slideCount = this.slides.length;

      if (!this.sliderEnabled) {
        this.disableSliderMode();
        return;
      }

      if (this.slideCount === 0) {
        this.progress.style.transform = 'scaleX(0)';
        return;
      }

      this.currentIndex = Math.min(this.currentIndex, this.slideCount - 1);

      if (this.desktopQuery.matches) {
        this.enableDesktopScrollLockMode();
      } else {
        this.resetDesktopMobileState();
      }
    }

    resetDesktopMobileState() {
      this.slides.forEach((slide) => {
        slide.style.opacity = '';
        slide.classList.remove('is-active');
      });

      this.track.style.transform = '';
      this.scrollSpace.style.height = '';

      if (this.sticky) {
        this.sticky.style.top = '';
      }

      this.progress.style.transform = 'scaleX(0.15)';
      this.updateCounter(0, 0);

      if (this.prevBtn) this.prevBtn.disabled = true;
      if (this.nextBtn) this.nextBtn.disabled = true;
    }

    disableSliderMode() {
      this.slides.forEach((slide) => {
        slide.style.opacity = '';
        slide.classList.remove('is-active');
      });

      this.track.style.transform = '';
      this.scrollSpace.style.height = '';

      if (this.sticky) {
        this.sticky.style.top = '';
      }

      if (this.progress) {
        this.progress.style.transform = '';
      }

      if (this.counter) {
        this.counter.style.left = '';
      }

      if (this.counterCurrent) {
        this.counterCurrent.textContent = this.formatSlideNumber(1);
      }

      if (this.prevBtn) this.prevBtn.disabled = true;
      if (this.nextBtn) this.nextBtn.disabled = true;
    }

    enableDesktopScrollLockMode() {
      const headerHeight = this.getThemeHeaderHeight();
      const stickyHeight = this.sticky ? this.sticky.getBoundingClientRect().height : this.getBoundingClientRect().height;
      const viewportWidth = this.getBoundingClientRect().width;

      const centeredOffset = Math.max((window.innerHeight - stickyHeight) / 2, 0);
      this.stickyTop = Math.max(centeredOffset, headerHeight);

      if (this.sticky) {
        this.sticky.style.top = `${Math.round(this.stickyTop)}px`;
      }

      this.slideWidth = viewportWidth;
      this.maxTranslate = Math.max((this.slideCount - 1) * this.slideWidth, 0);

      const scrollSpaceRect = this.scrollSpace.getBoundingClientRect();
      this.pinStart = window.scrollY + scrollSpaceRect.top - this.stickyTop;
      this.pinEnd = this.pinStart + this.maxTranslate;

      this.scrollSpace.style.height = `${stickyHeight + this.maxTranslate}px`;
      this.updateFromScrollPosition();
    }

    getThemeHeaderHeight() {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--theme-header-height') || '0';
      const parsed = parseFloat(raw);
      return Number.isNaN(parsed) ? 0 : parsed;
    }

    formatSlideNumber(value) {
      const num = Math.max(1, Number(value) || 1);
      return num < 10 ? `0${num}` : `${num}`;
    }

    updateCounter(progress, activeIndex) {
      if (this.counterCurrent) {
        this.counterCurrent.textContent = this.formatSlideNumber(activeIndex + 1);
      }

      if (!this.counter) return;

      const boundedProgress = Math.min(Math.max(progress, 0), 1);
      const minX = 0.2;
      const maxX = 0.9;
      const visualProgress = minX + ((maxX - minX) * boundedProgress);
      this.counter.style.left = `${visualProgress * 100}%`;
    }

    getCurrentIndexFromScroll() {
      if (this.slideCount <= 1 || this.maxTranslate <= 0) return 0;
      const travelled = Math.min(Math.max(window.scrollY - this.pinStart, 0), this.maxTranslate);
      const progress = travelled / this.maxTranslate;
      return Math.round(progress * (this.slideCount - 1));
    }

    scrollToIndex(index) {
      if (this.slideCount <= 1) return;
      const clampedIndex = Math.max(0, Math.min(index, this.slideCount - 1));
      const targetTranslate = clampedIndex * this.slideWidth;
      const targetY = this.pinStart + targetTranslate;
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }

    updateFromScrollPosition() {
      if (!this.desktopQuery.matches || !this.sliderEnabled) return;

      const travelled = Math.min(Math.max(window.scrollY - this.pinStart, 0), this.maxTranslate);
      const progress = this.maxTranslate > 0 ? travelled / this.maxTranslate : 1;
      const activeIndex = this.slideCount > 1 ? Math.round(progress * (this.slideCount - 1)) : 0;
      const progressWithOffset = this.slideCount > 1 ? 0.15 + (progress * 0.85) : 1;

      this.track.style.transform = `translate3d(${-travelled}px, 0, 0)`;
      this.progress.style.transform = `scaleX(${progressWithOffset})`;
      this.updateCounter(progress, activeIndex);

      this.currentIndex = activeIndex;
      this.slides[activeIndex].querySelector('video')?.play().catch(()=>{console.log('video not playing')});
      if (this.prevBtn) this.prevBtn.disabled = activeIndex <= 0;
      if (this.nextBtn) this.nextBtn.disabled = activeIndex >= this.slideCount - 1;
    }

    onScroll() {
      if (!this.desktopQuery.matches || !this.sliderEnabled) return;
      if (this.ticking) return;

      this.ticking = true;
      requestAnimationFrame(() => {
        this.updateFromScrollPosition();
        this.ticking = false;
      });
    }

    onResize() {
      if (!this.sliderEnabled) return;
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.setup(), 120);
    }

    onPrevClick() {
      if (!this.desktopQuery.matches) return;
      this.scrollToIndex(this.getCurrentIndexFromScroll() - 1);
    }

    onNextClick() {
      if (!this.desktopQuery.matches) return;
      this.scrollToIndex(this.getCurrentIndexFromScroll() + 1);
    }

    onBreakpointChange() {
      this.setup();
    }

    disconnectedCallback() {
      if (this.sliderEnabled) {
        this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrevClick);
        if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNextClick);
      }
    }
  }

  customElements.define('horizontal-scroll-showcase', HorizontalScrollShowcase);
}
